/* M6.6 — COD Disputes Queue. Replaces the external dispute tracker. Lifecycle:
   Open → Sent to Ops → Status Corrected → Reversal Generated → Resolved /
   Rejected. Row click opens a detail drawer with a stepper, the POC, an audit
   timeline and Advance / Resolve / Reject actions (each captures a reason). */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {SupplyTable, Badge, SideDrawer, ConfirmDialog, useToast, inr, fmtDateTime, relLabel} from '../components'
import {VerticalChip} from './codBits'
import {
  visibleDisputes,
  disputeVertical,
  disputeStep,
  disputeBadgeKey,
  DISPUTE_LIFECYCLE,
  MISMATCH_TYPES,
  nextStatus,
  transition,
  podPocFor,
} from './codLogic'

export default function DisputesTab() {
  const toast = useToast()
  const [, force] = useState(0)
  const refresh = () => force((n) => n + 1)
  const [detail, setDetail] = useState(null)
  const [confirm, setConfirm] = useState(null)

  const rows = visibleDisputes()

  const advance = (d) => {
    const nx = nextStatus(d.status)
    if (!nx) {
      toast('This dispute is at the end of the active workflow — resolve or reject it instead.', 'info')
      return
    }
    setConfirm({
      title: 'Advance to "' + nx + '"?',
      okLabel: 'Advance',
      needReason: true,
      body:
        'Move dispute ' +
        d.awb +
        ' from "' +
        d.status +
        '" to "' +
        nx +
        '". Every transition records actor, timestamp, status and reason.',
      onOk: (reason) => {
        transition(d, nx, reason)
        refresh()
        toast('Dispute ' + d.awb + ' → ' + nx + '.', 'success')
        if (detail && detail.awb === d.awb) setDetail({...d})
      },
    })
  }
  const resolve = (d) => {
    setConfirm({
      title: 'Resolve dispute ' + d.awb + '?',
      okLabel: 'Resolve',
      needReason: true,
      body: 'Confirm recovery and close. A closing reason and final status are required.',
      onOk: (reason) => {
        transition(d, 'Resolved', reason)
        setDetail(null)
        refresh()
        toast('Dispute ' + d.awb + ' resolved.', 'success')
      },
    })
  }
  const reject = (d) => {
    setConfirm({
      title: 'Reject dispute ' + d.awb + '?',
      okLabel: 'Reject',
      danger: true,
      needReason: true,
      body: 'Close this dispute as rejected. A reason is required.',
      onOk: (reason) => {
        transition(d, 'Rejected', reason)
        setDetail(null)
        refresh()
        toast('Dispute ' + d.awb + ' rejected.', 'success')
      },
    })
  }

  const columns = [
    {key: 'awb', label: 'AWB', render: (d) => <span className='mono'>{d.awb}</span>},
    {
      key: 'courier',
      label: 'Courier',
      render: (d) => (
        <>
          <b>{d.courier}</b> <VerticalChip vertical={disputeVertical(d)} />
        </>
      ),
      exportValue: (d) => d.courier,
    },
    {
      key: 'cpCode',
      label: 'CP Account Code',
      render: (d) => (
        <span className='mono' style={{fontSize: 12}}>
          {d.cpCode}
        </span>
      ),
    },
    {
      key: 'codValue',
      label: 'COD Value',
      align: 'right',
      render: (d) => <b className='mono'>{inr(d.codValue)}</b>,
      value: (d) => d.codValue,
    },
    {key: 'proshipStatus', label: 'ProShip Status'},
    {key: 'courierStatus', label: 'Courier Status'},
    {key: 'mismatch', label: 'Mismatch Type', render: (d) => <span className='tag'>{d.mismatch}</span>},
    {
      key: 'status',
      label: 'Dispute Status',
      render: (d) => <Badge status={disputeBadgeKey(d.status)} label={d.status} />,
    },
    {key: 'owner', label: 'Owner', render: (d) => <span style={{fontSize: 12}}>{d.owner}</span>},
    {
      key: 'age',
      label: 'Age',
      align: 'center',
      render: (d) =>
        d.status === 'Resolved' || d.status === 'Rejected' ? (
          <span className='muted'>{d.age}d</span>
        ) : d.age > 7 ? (
          <span className='mini-pill mp-red'>{d.age}d</span>
        ) : (
          <span className='tag'>{d.age}d</span>
        ),
      value: (d) => d.age,
    },
    {key: 'lastUpdate', label: 'Last Update', render: (d) => relLabel(d.lastUpdate), value: (d) => d.lastUpdate},
  ]

  const filters = [
    {
      key: 'status',
      label: 'Dispute Status',
      options: DISPUTE_LIFECYCLE.map((s) => ({value: s, label: s})),
      predicate: (d, sel) => sel.has(d.status),
    },
    {
      key: 'mismatch',
      label: 'Mismatch Type',
      options: MISMATCH_TYPES.map((t) => ({value: t, label: t})),
      predicate: (d, sel) => sel.has(d.mismatch),
    },
  ]

  const rowActions = (d) => [
    {label: 'View / manage', icon: <VisibilityOutlinedIcon fontSize='small' />, onClick: () => setDetail(d)},
    d.status !== 'Resolved' && d.status !== 'Rejected'
      ? {label: 'Advance status', icon: <SyncOutlinedIcon fontSize='small' />, onClick: () => advance(d)}
      : null,
  ]

  return (
    <div>
      <div className='page-head' style={{marginBottom: 14}}>
        <div>
          <div className='seclabel' style={{marginBottom: 4}}>
            COD Disputes Queue
          </div>
          <p className='sub' style={{margin: 0}}>
            Replaces the external dispute tracker. Lifecycle: Open → Sent to Ops → Status Corrected →
            Reversal Generated → Resolved / Rejected.
          </p>
        </div>
      </div>

      <SupplyTable
        rows={rows}
        rowKey={(d) => d.awb}
        columns={columns}
        searchKeys={['awb', 'courier', 'cpCode', 'owner', 'mismatch']}
        searchPlaceholder='Search AWB, courier, owner, mismatch...'
        defaultSort={{key: 'lastUpdate', dir: 'desc'}}
        exportName='cod-disputes'
        emptyText='No disputes raised. Raise one from the Reconciliation Workspace.'
        filters={filters}
        rowActions={rowActions}
        rowClick={(d) => setDetail(d)}
      />

      <DisputeDetail
        d={detail}
        onClose={() => setDetail(null)}
        onAdvance={advance}
        onResolve={resolve}
        onReject={reject}
      />

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm ? confirm.title : ''}
        body={confirm ? confirm.body : ''}
        needReason={confirm ? confirm.needReason : false}
        okLabel={confirm ? confirm.okLabel : ''}
        danger={confirm ? !!confirm.danger : false}
        onOk={(reason) => {
          if (confirm) confirm.onOk(reason)
          setConfirm(null)
        }}
      />
    </div>
  )
}

function DisputeDetail({d, onClose, onAdvance, onResolve, onReject}) {
  if (!d) return null
  const done = d.status === 'Resolved' || d.status === 'Rejected'
  const poc = podPocFor(d.courier)
  const cur = disputeStep(d.status)
  const steps = ['Open', 'Sent to Ops', 'Status Corrected', 'Reversal Generated', 'Resolved']
  const hist = (d.history || [{status: d.status, by: d.owner, at: d.lastUpdate, reason: d.note || ''}])
    .slice()
    .reverse()
  const nx = nextStatus(d.status)

  let footer
  if (done) {
    footer = (
      <button className='btn' onClick={onClose}>
        Close
      </button>
    )
  } else {
    footer = (
      <>
        <button className='btn btn-danger' onClick={() => onReject(d)}>
          <CloseIcon style={{width: 16, height: 16}} />
          Reject
        </button>
        <button className={'btn' + (nx ? '' : ' btn-primary')} onClick={() => onResolve(d)}>
          <CheckCircleOutlineIcon style={{width: 16, height: 16}} />
          Resolve
        </button>
        {nx ? (
          <button className='btn btn-primary' onClick={() => onAdvance(d)}>
            <SyncOutlinedIcon style={{width: 16, height: 16}} />
            Advance to {nx}
          </button>
        ) : null}
      </>
    )
  }

  return (
    <SideDrawer open={!!d} onClose={onClose} title={'Dispute · ' + d.awb} width={540} footer={footer}>
      <div className='stepper' style={{margin: '0 0 18px'}}>
        {steps.map((s, i) => {
          const cls =
            d.status === 'Rejected'
              ? i === 0
                ? 'active'
                : ''
              : i < cur
                ? 'done'
                : i === cur
                  ? 'active'
                  : ''
          return (
            <div className={'step ' + cls} key={s}>
              <span className='num'>{i < cur ? '✓' : i + 1}</span>
              <span className='lbl' style={{whiteSpace: 'normal', fontSize: 11.5, lineHeight: 1.2}}>
                {s}
              </span>
              {i < 4 ? <span className='bar' /> : null}
            </div>
          )
        })}
      </div>

      <div className='dl' style={{gridTemplateColumns: 'auto 1fr', marginBottom: 14}}>
        <dt>AWB</dt>
        <dd className='mono'>{d.awb}</dd>
        <dt>Courier / CP code</dt>
        <dd>
          {d.courier} ·{' '}
          <span className='mono' style={{fontSize: 12}}>
            {d.cpCode}
          </span>{' '}
          <VerticalChip vertical={disputeVertical(d)} />
        </dd>
        <dt>COD Value at risk</dt>
        <dd>
          <b style={{color: 'var(--error)'}}>{inr(d.codValue)}</b>
        </dd>
        <dt>Mismatch</dt>
        <dd>
          <span className='tag'>{d.mismatch}</span> ({d.proshipStatus} ↔ {d.courierStatus})
        </dd>
        <dt>Owner</dt>
        <dd>{d.owner}</dd>
        <dt>COD Remittance POC</dt>
        <dd>
          {poc ? (
            <>
              {poc.name} ·{' '}
              <a className='lnk' href={'mailto:' + poc.email}>
                {poc.email}
              </a>
            </>
          ) : (
            <span className='muted'>Not on file</span>
          )}
        </dd>
      </div>

      <div className='warnbox info' style={{margin: '0 0 14px'}}>
        <span style={{display: 'flex'}}>
          <InfoOutlinedIcon style={{width: 16, height: 16}} />
        </span>
        <div>
          A structured Ops/Tech task verifies the true status with the courier and corrects the
          ProShip status if confirmed — the analyst never needs status-edit rights.
        </div>
      </div>

      <div className='seclabel' style={{marginBottom: 10}}>
        Audit trail
      </div>
      <div className='timeline'>
        {hist.map((h, i) => (
          <div
            className={
              'tl-item' + (h.status === 'Rejected' ? ' red' : h.status === 'Resolved' ? ' green' : '')
            }
            key={i}
          >
            <div className='tl-card'>
              <div className='row between center'>
                <b>{h.status}</b>
                <span className='muted' style={{fontSize: 12}}>
                  {fmtDateTime(h.at)}
                </span>
              </div>
              <div className='tl-meta'>
                by {h.by}
                {h.reason ? ' · ' + h.reason : ''}
              </div>
            </div>
          </div>
        ))}
      </div>
    </SideDrawer>
  )
}
DisputeDetail.propTypes = {
  d: PropTypes.object,
  onClose: PropTypes.func,
  onAdvance: PropTypes.func,
  onResolve: PropTypes.func,
  onReject: PropTypes.func,
}
